import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useState } from "react";
import useColors from "../../../../../api/hooks/useColors";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { icons } from "../../../../stack/environment/icons/iconExport";
import { DURATION_TYPES, SNOOZE_PERIODS } from "./constants";
import {
  calculateDaysFromDate,
  getMaxDateDayjs,
  getMinDateDayjs,
} from "./utils";

const OpenPickerIcon = () => (
  <SvgToIcon IconLogo={icons.TimeframeIcon} width="18px" />
);

export default function DurationSelector({
  durationMode,
  onDurationChange,
  selectedDays: selectedDaysProp,
  onSelectedDaysChange,
}) {
  const colors = useColors();
  const [localIsCustom, setLocalIsCustom] = useState(false);
  const [localSelectedDate, setLocalSelectedDate] = useState(null);

  const SelectChevronIcon = () => (
    <SvgToIcon
      IconLogo={icons.chevronDownIcon}
      color={colors.icon_action_active}
      width="20px"
    />
  );

  const selectedDays =
    selectedDaysProp ??
    (durationMode?.type === DURATION_TYPES.PRESET
      ? Number(durationMode?.value || 0)
      : 0);

  const handlePresetChange = (value) => {
    if (value === DURATION_TYPES.DATE || value === "date") {
      if (onSelectedDaysChange) onSelectedDaysChange(0);
      if (onDurationChange)
        onDurationChange({
          type: DURATION_TYPES.DATE,
          value: 0,
          selectedDate: null,
        });
      else {
        setLocalIsCustom(true);
        setLocalSelectedDate(null);
      }
      return;
    }

    const numericValue = Number(value) || 0;
    if (onSelectedDaysChange) onSelectedDaysChange(numericValue);
    if (onDurationChange)
      onDurationChange({
        type: DURATION_TYPES.PRESET,
        value: numericValue,
        selectedDate: null,
      });
    setLocalIsCustom(false);
  };

  const handleDateChange = (newDate) => {
    const baseDate = newDate?.toDate?.() || newDate || null;
    const days = calculateDaysFromDate(baseDate);
    if (onSelectedDaysChange) {
      onSelectedDaysChange(days);
    }
    if (onDurationChange) {
      onDurationChange({
        type: DURATION_TYPES.DATE,
        value: days,
        selectedDate: baseDate,
      });
    }
    if (!onDurationChange) {
      setLocalSelectedDate(newDate ?? null);
    }
  };

  return (
    <>
      <Typography
        variant="overline"
        sx={{ marginBottom: "10px", color: colors.text_primary }}
      >
        How long do you want to snooze?
      </Typography>
      <FormControl fullWidth sx={{ marginBottom: "10px" }}>
        <Select
          value={
            durationMode?.type === DURATION_TYPES.DATE
              ? "date"
              : localIsCustom
              ? "date"
              : selectedDays
          }
          displayEmpty
          onChange={(e) => handlePresetChange(e.target.value)}
          IconComponent={SelectChevronIcon}
          renderValue={(selected) => {
            if (selected === 0) {
              return (
                <Typography
                  variant="caption"
                  sx={{ color: colors.icon_action_active }}
                >
                  Select a duration...
                </Typography>
              );
            }
            if (selected === "date") {
              return (
                <Typography
                  variant="caption"
                  sx={{ color: colors.text_primary }}
                >
                  Custom
                </Typography>
              );
            }
            return (
              <Typography variant="caption" sx={{ color: colors.text_primary }}>
                {selected} days
              </Typography>
            );
          }}
          sx={{
            borderRadius: "10px",
            border: `1px solid ${colors.grey_20percent}`,
            height: "32px",
            marginTop: "8px",
            paddingRight: "6px",
            "& .MuiSelect-select": {
              padding: "8px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&.Mui-focused .MuiOutlinedInput-root": {
              boxShadow: "none",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: "10px",
              },
            },
          }}
        >
          {SNOOZE_PERIODS.map((p) => (
            <MenuItem
              key={p.value}
              value={p.value}
              sx={{ borderRadius: "10px", margin: "8px", padding: "8px" }}
            >
              <Typography variant="caption" sx={{ color: colors.text_primary }}>
                {p.label}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {(durationMode?.type === DURATION_TYPES.DATE || localIsCustom) && (
        <Box sx={{ marginBottom: "10px" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={(() => {
                const raw = durationMode?.selectedDate ?? localSelectedDate;
                if (!raw) return null;
                return dayjs.isDayjs(raw) ? raw : dayjs(raw);
              })()}
              onChange={handleDateChange}
              minDate={getMinDateDayjs()}
              maxDate={getMaxDateDayjs()}
              format="MM/DD/YYYY"
              slots={{ openPickerIcon: OpenPickerIcon }}
              slotProps={{
                textField: {
                  placeholder: "Select end date...",
                  fullWidth: true,
                  size: "small",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      border: `1px solid ${colors.grey_20percent}`,
                      height: "32px",
                      paddingRight: "6px",
                      backgroundColor: colors.white,
                      "& input": {
                        padding: "8px",
                        fontSize: "0.75rem",
                        color: colors.text_primary,
                        cursor: "pointer",
                        "&::placeholder": {
                          color: colors.icon_action_active,
                          opacity: 1,
                        },
                      },
                      "& .MuiInputAdornment-root": {
                        marginRight: "8px",
                        "& .MuiIconButton-root": {
                          padding: "4px",
                          color: colors.text_secondary,
                          "&:hover": {
                            backgroundColor: colors.grey_8percent,
                          },
                        },
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      )}
    </>
  );
}
